/**
 * Lexicon Service
 *
 * CRUD operations for lexicon_transcripts, lexicon_speaker_analyses,
 * and lexicon_speaker_profiles.
 *
 * Also orchestrates the full analysis pipeline:
 *   createTranscript → analyzeTranscript → saveAnalyses → upsertProfiles
 */

import { getSupabaseClient } from './supabaseClient';
import { analyzeTranscript, detectSpeakers } from './lexiconAnalysisService';
export { detectSpeakers } from './lexiconAnalysisService';
import {
  deriveArchetype,
  ARCHETYPE_DESCRIPTIONS,
  type LexiconTranscript,
  type SpeakerAnalysis,
  type SpeakerProfile,
  type CreateTranscriptInput,
  type LlmSpeakerResult,
  type DetectedPattern,
} from '../types/prioritization/lexicon';

// ── Converters ────────────────────────────────────────────────────────────────

function rowToTranscript(row: Record<string, unknown>): LexiconTranscript {
  return {
    id:             row.id as string,
    workspaceId:    row.workspace_id as string,
    title:          row.title as string,
    meetingDate:    (row.meeting_date as string | null) ?? null,
    rawContent:     row.raw_content as string,
    speakers:       (row.speakers as string[]) ?? [],
    analysisStatus: row.analysis_status as LexiconTranscript['analysisStatus'],
    analysisError:  (row.analysis_error as string | null) ?? null,
    createdBy:      (row.created_by as string | null) ?? null,
    createdAt:      row.created_at as string,
    updatedAt:      row.updated_at as string,
  };
}

function rowToAnalysis(row: Record<string, unknown>): SpeakerAnalysis {
  return {
    id:                    row.id as string,
    transcriptId:          row.transcript_id as string,
    workspaceId:           row.workspace_id as string,
    userId:                (row.user_id as string | null) ?? null,
    speakerName:           row.speaker_name as string,
    specificityScore:      (row.specificity_score as number | null) ?? null,
    clarityScore:          (row.clarity_score as number | null) ?? null,
    flexibilityScore:      (row.flexibility_score as number | null) ?? null,
    agencyScore:           (row.agency_score as number | null) ?? null,
    momentumScore:         (row.momentum_score as number | null) ?? null,
    strategicIndex:        (row.strategic_index as number | null) ?? null,
    actionCommitmentRate:  (row.action_commitment_rate as number | null) ?? null,
    questionRate:          (row.question_rate as number | null) ?? null,
    hedgingFrequency:      (row.hedging_frequency as number | null) ?? null,
    topTopics:             (row.top_topics as string[]) ?? [],
    keyPhrases:            (row.key_phrases as string[]) ?? [],
    notableQuotes:         (row.notable_quotes as string[]) ?? [],
    detectedPatterns:      (row.detected_patterns as DetectedPattern[]) ?? [],
    strengths:             (row.strengths as string[]) ?? [],
    blindSpots:            (row.blind_spots as string[]) ?? [],
    recommendations:       (row.recommendations as string[]) ?? [],
    rawAnalysis:           (row.raw_analysis as Record<string, unknown> | null) ?? null,
    wordCount:             (row.word_count as number | null) ?? null,
    turnCount:             (row.turn_count as number | null) ?? null,
    createdAt:             row.created_at as string,
  };
}

function rowToProfile(row: Record<string, unknown>): SpeakerProfile {
  return {
    id:                       row.id as string,
    workspaceId:              row.workspace_id as string,
    userId:                   (row.user_id as string | null) ?? null,
    speakerName:              row.speaker_name as string,
    avgSpecificity:           (row.avg_specificity as number | null) ?? null,
    avgClarity:               (row.avg_clarity as number | null) ?? null,
    avgFlexibility:           (row.avg_flexibility as number | null) ?? null,
    avgAgency:                (row.avg_agency as number | null) ?? null,
    avgMomentum:              (row.avg_momentum as number | null) ?? null,
    avgStrategicIndex:        (row.avg_strategic_index as number | null) ?? null,
    avgActionCommitmentRate:  (row.avg_action_commitment_rate as number | null) ?? null,
    archetype:                (row.archetype as SpeakerProfile['archetype']) ?? null,
    archetypeDescription:     (row.archetype_description as string | null) ?? null,
    consistentStrengths:      (row.consistent_strengths as string[]) ?? [],
    persistentBlindSpots:     (row.persistent_blind_spots as string[]) ?? [],
    growthAreas:              (row.growth_areas as string[]) ?? [],
    topPatterns:              (row.top_patterns as DetectedPattern[]) ?? [],
    topTopics:                (row.top_topics as string[]) ?? [],
    transcriptCount:          (row.transcript_count as number) ?? 0,
    totalWordCount:           (row.total_word_count as number) ?? 0,
    updatedAt:                row.updated_at as string,
  };
}

// ── Transcript CRUD ───────────────────────────────────────────────────────────

export async function listTranscripts(workspaceId: string): Promise<{
  data: LexiconTranscript[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('lexicon_transcripts')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) return { data: [], error: error.message };
    return {
      data: (data ?? []).map(r => rowToTranscript(r as Record<string, unknown>)),
      error: null,
    };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function getTranscript(id: string): Promise<{
  data: LexiconTranscript | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('lexicon_transcripts')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) return { data: null, error: error.message };
    if (!data) return { data: null, error: null };
    return { data: rowToTranscript(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function createTranscript(input: CreateTranscriptInput): Promise<{
  data: LexiconTranscript | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const speakers = detectSpeakers(input.rawContent);

    const { data, error } = await supabase
      .from('lexicon_transcripts')
      .insert({
        workspace_id:     input.workspaceId,
        title:            input.title,
        meeting_date:     input.meetingDate ?? null,
        raw_content:      input.rawContent,
        speakers,
        analysis_status: 'pending',
        created_by:      input.createdBy ?? null,
      })
      .select()
      .single();

    if (error) return { data: null, error: error.message };
    return { data: rowToTranscript(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function deleteTranscript(id: string): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from('lexicon_transcripts').delete().eq('id', id);
    return { error: error?.message ?? null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ── Analysis CRUD ─────────────────────────────────────────────────────────────

export async function getAnalysesForTranscript(transcriptId: string): Promise<{
  data: SpeakerAnalysis[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('lexicon_speaker_analyses')
      .select('*')
      .eq('transcript_id', transcriptId)
      .order('speaker_name');

    if (error) return { data: [], error: error.message };
    return {
      data: (data ?? []).map(r => rowToAnalysis(r as Record<string, unknown>)),
      error: null,
    };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ── Speaker Profiles ──────────────────────────────────────────────────────────

export async function listSpeakerProfiles(workspaceId: string): Promise<{
  data: SpeakerProfile[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('lexicon_speaker_profiles')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('transcript_count', { ascending: false });

    if (error) return { data: [], error: error.message };
    return {
      data: (data ?? []).map(r => rowToProfile(r as Record<string, unknown>)),
      error: null,
    };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function getSpeakerProfile(workspaceId: string, speakerName: string): Promise<{
  data: SpeakerProfile | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('lexicon_speaker_profiles')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('speaker_name', speakerName)
      .maybeSingle();

    if (error) return { data: null, error: error.message };
    if (!data) return { data: null, error: null };
    return { data: rowToProfile(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ── Analysis Pipeline ─────────────────────────────────────────────────────────

/**
 * Full pipeline: create transcript → analyze → save results → update profiles.
 * Returns the transcript with status set to 'complete' or 'failed'.
 */
export async function createAndAnalyzeTranscript(input: CreateTranscriptInput): Promise<{
  data: { transcript: LexiconTranscript; analyses: SpeakerAnalysis[] } | null;
  error: string | null;
}> {
  // Step 1: persist the raw transcript
  const { data: transcript, error: createError } = await createTranscript(input);
  if (createError || !transcript) {
    return { data: null, error: createError ?? 'Failed to create transcript' };
  }

  return runAnalysisPipeline(transcript, input.workspaceId);
}

/**
 * Re-run analysis on an existing transcript.
 */
export async function reanalyzeTranscript(transcriptId: string, workspaceId: string): Promise<{
  data: { transcript: LexiconTranscript; analyses: SpeakerAnalysis[] } | null;
  error: string | null;
}> {
  const { data: transcript, error: fetchError } = await getTranscript(transcriptId);
  if (fetchError || !transcript) {
    return { data: null, error: fetchError ?? 'Transcript not found' };
  }
  return runAnalysisPipeline(transcript, workspaceId);
}

async function runAnalysisPipeline(
  transcript: LexiconTranscript,
  workspaceId: string
): Promise<{
  data: { transcript: LexiconTranscript; analyses: SpeakerAnalysis[] } | null;
  error: string | null;
}> {
  const supabase = getSupabaseClient();

  // Step 2: mark processing
  await supabase
    .from('lexicon_transcripts')
    .update({ analysis_status: 'processing' })
    .eq('id', transcript.id);

  // Step 3: call LLM
  const { data: llmOutput, error: llmError } = await analyzeTranscript({
    transcriptId:   transcript.id,
    rawContent:     transcript.rawContent,
    title:          transcript.title,
    workspaceId,
  });

  if (llmError || !llmOutput) {
    await supabase
      .from('lexicon_transcripts')
      .update({ analysis_status: 'failed', analysis_error: llmError })
      .eq('id', transcript.id);
    return { data: null, error: llmError ?? 'LLM analysis returned no output' };
  }

  // Step 4: save speaker analyses
  const analyses: SpeakerAnalysis[] = [];
  for (const result of llmOutput.speakers) {
    const saved = await saveSpeakerAnalysis(transcript.id, workspaceId, result);
    if (saved) analyses.push(saved);

    // Step 5: upsert speaker profile
    await supabase.rpc('upsert_lexicon_speaker_profile', {
      p_workspace_id:  workspaceId,
      p_speaker_name:  result.speakerName,
      p_user_id:       null,
    });

    // Step 6: update archetype on profile
    await updateProfileArchetype(workspaceId, result.speakerName);
  }

  // Step 7: mark complete
  await supabase
    .from('lexicon_transcripts')
    .update({
      analysis_status: 'complete',
      analysis_error:  null,
      speakers: llmOutput.speakers.map(s => s.speakerName),
    })
    .eq('id', transcript.id);

  const { data: updatedTranscript } = await getTranscript(transcript.id);

  return {
    data: { transcript: updatedTranscript ?? transcript, analyses },
    error: null,
  };
}

async function saveSpeakerAnalysis(
  transcriptId: string,
  workspaceId: string,
  result: LlmSpeakerResult
): Promise<SpeakerAnalysis | null> {
  try {
    const supabase = getSupabaseClient();

    // Delete any prior analysis for this speaker on this transcript (re-run case)
    await supabase
      .from('lexicon_speaker_analyses')
      .delete()
      .eq('transcript_id', transcriptId)
      .eq('speaker_name', result.speakerName);

    const { data, error } = await supabase
      .from('lexicon_speaker_analyses')
      .insert({
        transcript_id:          transcriptId,
        workspace_id:           workspaceId,
        speaker_name:           result.speakerName,
        specificity_score:      result.scores.specificity,
        clarity_score:          result.scores.clarity,
        flexibility_score:      result.scores.flexibility,
        agency_score:           result.scores.agency,
        momentum_score:         result.scores.momentum,
        strategic_index:        result.scores.strategicIndex,
        action_commitment_rate: result.actionCommitmentRate,
        question_rate:          result.questionRate,
        hedging_frequency:      result.hedgingFrequency,
        top_topics:             result.topTopics,
        key_phrases:            result.keyPhrases,
        notable_quotes:         result.notableQuotes,
        detected_patterns:      result.detectedPatterns,
        strengths:              result.strengths,
        blind_spots:            result.blindSpots,
        recommendations:        result.recommendations,
        raw_analysis:           result,
        word_count:             result.wordCount,
        turn_count:             result.turnCount,
      })
      .select()
      .single();

    if (error || !data) return null;
    return rowToAnalysis(data as Record<string, unknown>);
  } catch {
    return null;
  }
}

async function updateProfileArchetype(workspaceId: string, speakerName: string): Promise<void> {
  try {
    const supabase = getSupabaseClient();
    const { data } = await supabase
      .from('lexicon_speaker_profiles')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('speaker_name', speakerName)
      .maybeSingle();

    if (!data) return;
    const profile = rowToProfile(data as Record<string, unknown>);
    const archetype = deriveArchetype(profile);
    const archetypeDescription = ARCHETYPE_DESCRIPTIONS[archetype];

    await supabase
      .from('lexicon_speaker_profiles')
      .update({ archetype, archetype_description: archetypeDescription })
      .eq('workspace_id', workspaceId)
      .eq('speaker_name', speakerName);
  } catch {
    // best-effort
  }
}
